import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Icons,
  Input,
} from '@commenda/kakashi';
import {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import mixpanel from '@commenda/mixpanel';
import { cn } from '@commenda/utils';
import { useState } from 'react';
import { EmptyStateV3 } from '../components';
import { TTemplate } from './TEMPLATES';

interface TemplatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: TTemplate[];
  onSelectTemplate: (template: TTemplate) => void;
}

const TemplatesModal = ({
  isOpen,
  onClose,
  templates,
  onSelectTemplate,
}: TemplatesModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredTemplateId, setHoveredTemplateId] = useState<string | null>(
    null
  );

  const filteredTemplates = templates.filter((template) =>
    template.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl overflow-clip">
        <DialogHeader className="flex flex-col gap-xs items-start">
          <DialogTitle>Select a response template</DialogTitle>
          <DialogDescription>
            Below is a list of saved response templates. Select an option and
            make edits as needed.
          </DialogDescription>
        </DialogHeader>

        <div className="sticky top-0 bg-bg-primary border-b border-border-primary p-xl flex flex-col gap-xs">
          <Input
            placeholder="Search template name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftEl={() => (
              <Icons.SearchLg className="size-4 text-fg-quaternary" />
            )}
          />
          <div className="txt-small-regular text-text-tertiary shrink-0">
            Showing {filteredTemplates.length} results
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="max-h-[50vh] overflow-y-auto">
          <div className="divide-y divide-border-primary">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="p-xl hover:bg-bg-secondary transition-colors group"
                onMouseEnter={() => setHoveredTemplateId(template.id)}
                onMouseLeave={() => setHoveredTemplateId(null)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-md mb-sm">
                      <span className="txt-small-bold text-text-primary">
                        {template.title}
                      </span>
                    </div>
                    <p className="txt-small-regular text-text-secondary line-clamp-2">
                      {template.description}
                    </p>
                  </div>

                  <div
                    className={cn(
                      'ml-md transition-opacity duration-200',
                      hoveredTemplateId === template.id
                        ? 'opacity-100'
                        : 'opacity-0'
                    )}
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        mixpanel.track(MIXPANEL_EVENTS.USING_RESPONSE_TEMPLATES, {
                          surface_area: MIXPANEL_SURFACE_AREA.AGENT_APP,
                          template_id: template.id,
                        });
                        onSelectTemplate(template);
                      }}
                      className="whitespace-nowrap"
                    >
                      <Icons.Check className="size-4 mr-xs" />
                      Use template
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="p-xl">
              <EmptyStateV3
                title={`No results found for "${searchQuery}".`}
                description="Check for typing errors or change your search."
                className="bg-bg-disabled-subtle"
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplatesModal;
